import type { MessageAttachment } from '@shared/all/domains/messages';
import type { Post } from '@shared/all/domains/posts';
import type {
  ActivityStatus,
  BoardScope,
  CreateRoomResponse,
  JoinRoomResponse,
  MemberAddedPayload,
  MessageSender,
  NewConversationPayload,
  ResearchError,
  ResearchProgress,
  ResearchResult,
  RTCIceCandidateInit,
  RTCSessionDescriptionInit,
  ScrapeStatusUpdate,
  SearchIndexProgress,
  SocketConversationParticipant,
  SocketUserTag
} from '@shared/all/domains/socket';

// Re-export types for consumers
export type {
  ActivityStatus,
  MemberAddedPayload,
  MessageSender,
  NewConversationPayload,
  ScrapeStatusUpdate,
  SocketConversationParticipant,
  SocketUserTag
};

import { openConversationRoom } from '../socket/conversationRooms';
import socket from '../socket/socket';

/**
 * Consolidated socket hook for real-time communication.
 * Provides typed methods for all socket events.
 *
 * Usage:
 * ```tsx
 * const { onNewMessage, emitTyping } = useSocket();
 *
 * useEffect(() => {
 *   const cleanup = onNewMessage((sender, convId, message, messageId, createdAt) => {
 *     // handle new message
 *   });
 *   return cleanup;
 * }, [conversationId]);
 * ```
 */
export function useSocket() {
  // ============================================
  // Global / Session Events
  // ============================================

  const onMessage = (callback: (message: string) => void) => {
    socket.on('message', callback);
    return () => {
      socket.off('message', callback);
    };
  };

  const onRefreshUser = (callback: () => void) => {
    socket.on('refreshUser', callback);
    return () => {
      socket.off('refreshUser', callback);
    };
  };

  const onSearchIndexProgress = (
    callback: (payload: SearchIndexProgress) => void
  ) => {
    socket.on('searchIndexProgress', callback);
    return () => {
      socket.off('searchIndexProgress', callback);
    };
  };

  const onResearchProgress = (
    callback: (payload: ResearchProgress) => void
  ) => {
    socket.on('researchProgress', callback);
    return () => {
      socket.off('researchProgress', callback);
    };
  };

  const onResearchResult = (callback: (payload: ResearchResult) => void) => {
    socket.on('researchResult', callback);
    return () => {
      socket.off('researchResult', callback);
    };
  };

  const onResearchError = (callback: (payload: ResearchError) => void) => {
    socket.on('researchError', callback);
    return () => {
      socket.off('researchError', callback);
    };
  };

  const onTronReward = (
    callback: (quantity: number, title: string) => void
  ) => {
    socket.on('tronReward', callback);
    return () => {
      socket.off('tronReward', callback);
    };
  };

  const onLogout = (callback: () => void) => {
    socket.on('logout', callback);
    return () => {
      socket.off('logout', callback);
    };
  };

  const onConnect = (callback: () => void) => {
    socket.on('connect', callback);
    return () => {
      socket.off('connect', callback);
    };
  };

  const onNotificationCount = (
    callback: (
      count: number,
      detail?: { type: string; username: string }
    ) => void
  ) => {
    socket.on('notificationCount', callback);
    return () => {
      socket.off('notificationCount', callback);
    };
  };

  // ============================================
  // Conversation Events
  // ============================================

  const onNewMessage = (
    callback: (
      sender: MessageSender,
      conversationId: string,
      message: string,
      messageId: number,
      createdAt: Date,
      attachments?: MessageAttachment[],
      /** Non-null for thread replies — skip those in the main transcript. */
      parentId?: number | null
    ) => void
  ) => {
    socket.on('newMessage', callback);
    return () => {
      socket.off('newMessage', callback);
    };
  };

  const onNewConversation = (
    callback: (conversation: NewConversationPayload) => void
  ) => {
    socket.on('newConversation', callback);
    return () => {
      socket.off('newConversation', callback);
    };
  };

  const onConversationNameUpdated = (
    callback: (conversationId: string, name: string) => void
  ) => {
    socket.on('conversationNameUpdated', callback);
    return () => {
      socket.off('conversationNameUpdated', callback);
    };
  };

  const onMemberAdded = (
    callback: (conversationId: string, member: MemberAddedPayload) => void
  ) => {
    socket.on('memberAdded', callback);
    return () => {
      socket.off('memberAdded', callback);
    };
  };

  const onMemberRemoved = (
    callback: (conversationId: string, memberId: number) => void
  ) => {
    socket.on('memberRemoved', callback);
    return () => {
      socket.off('memberRemoved', callback);
    };
  };

  const onBanDisputeRejected = (callback: (conversationId: string) => void) => {
    socket.on('banDisputeRejected', callback);
    return () => {
      socket.off('banDisputeRejected', callback);
    };
  };

  const onConversationDeleted = (
    callback: (conversationId: string) => void
  ) => {
    socket.on('conversationDeleted', callback);
    return () => {
      socket.off('conversationDeleted', callback);
    };
  };

  const onMessageAcknowledged = (
    callback: (messageId: number, conversationId: string) => void
  ) => {
    socket.on('messageAcknowledged', callback);
    return () => {
      socket.off('messageAcknowledged', callback);
    };
  };

  const onMessageReaction = (
    callback: (
      messageId: number,
      conversationId: string,
      emoji: string,
      userId: number,
      added: boolean,
      count: number
    ) => void
  ) => {
    socket.on('messageReaction', callback);
    return () => {
      socket.off('messageReaction', callback);
    };
  };

  const onThreadReplyCount = (
    callback: (parentId: number, conversationId: string, count: number) => void
  ) => {
    socket.on('threadReplyCount', callback);
    return () => {
      socket.off('threadReplyCount', callback);
    };
  };

  const onMessageEdited = (
    callback: (
      messageId: number,
      conversationId: string,
      content: string
    ) => void
  ) => {
    socket.on('messageEdited', callback);
    return () => {
      socket.off('messageEdited', callback);
    };
  };

  const onMessageDeleted = (
    callback: (messageId: number, conversationId: string) => void
  ) => {
    socket.on('messageDeleted', callback);
    return () => {
      socket.off('messageDeleted', callback);
    };
  };

  const onMessageRestored = (
    callback: (
      messageId: number,
      conversationId: string,
      content: string,
      attachments: MessageAttachment[]
    ) => void
  ) => {
    socket.on('messageRestored', callback);
    return () => {
      socket.off('messageRestored', callback);
    };
  };

  const onTyping = (
    callback: (conversationId: string, senderId: number) => void
  ) => {
    socket.on('typing', callback);
    return () => {
      socket.off('typing', callback);
    };
  };

  const emitTyping = (conversationId: string) => {
    socket.emit('typing', conversationId);
  };

  // Joins the socket room of a channel the user has opened; a DM or a group
  // is already joined. See ../socket/conversationRooms.ts.
  const joinConversationRoom = openConversationRoom;

  const onActivity = (
    callback: (status: ActivityStatus, conversationId: string) => void
  ) => {
    socket.on('activity', callback);
    return () => {
      socket.off('activity', callback);
    };
  };

  // ============================================
  // Post Events
  // ============================================

  const joinPostStats = (postId: string) => {
    socket.emit('joinPostStats', postId);
  };

  const leavePostStats = (postId: string) => {
    socket.emit('leavePostStats', postId);
  };

  const onRepliesCount = (
    callback: (count: number, postId: string) => void
  ) => {
    socket.on('repliesCount', callback);
    return () => {
      socket.off('repliesCount', callback);
    };
  };

  const onNewReply = (callback: (post: Post) => void) => {
    socket.on('newReply', callback);
    return () => {
      socket.off('newReply', callback);
    };
  };

  const onVoteCount = (callback: (count: number, postId: string) => void) => {
    socket.on('voteCount', callback);
    return () => {
      socket.off('voteCount', callback);
    };
  };

  // ============================================
  // Space Events
  // ============================================

  const joinBoardRoom = (spaceId: number) => {
    socket.emit('joinBoardRoom', spaceId);
  };

  const leaveBoardRoom = (spaceId: number) => {
    socket.emit('leaveBoardRoom', spaceId);
  };

  const onSpaceMeta = (callback: () => void) => {
    socket.on('spaceMeta', callback);
    return () => {
      socket.off('spaceMeta', callback);
    };
  };

  const onSpaceRules = (callback: () => void) => {
    socket.on('spaceRules', callback);
    return () => {
      socket.off('spaceRules', callback);
    };
  };

  const onSpaceMembers = (callback: () => void) => {
    socket.on('spaceMembers', callback);
    return () => {
      socket.off('spaceMembers', callback);
    };
  };

  const onSpaceDeleted = (callback: () => void) => {
    socket.on('spaceDeleted', callback);
    return () => {
      socket.off('spaceDeleted', callback);
    };
  };

  const onBoardUpdate = (
    callback: (scope: BoardScope, factiiiId?: number) => void
  ) => {
    socket.on('boardUpdate', callback);
    return () => {
      socket.off('boardUpdate', callback);
    };
  };

  const onPostCreated = (callback: (post: Post, factiiiId: number) => void) => {
    socket.on('postCreated', callback);
    return () => {
      socket.off('postCreated', callback);
    };
  };

  const onPostMoved = (
    callback: (post: Post, fromFactiiiId: number, toFactiiiId: number) => void
  ) => {
    socket.on('postMoved', callback);
    return () => {
      socket.off('postMoved', callback);
    };
  };

  const onPostReordered = (
    callback: (postId: string, factiiiId: number, pos: number) => void
  ) => {
    socket.on('postReordered', callback);
    return () => {
      socket.off('postReordered', callback);
    };
  };

  const onFactiiiReordered = (
    callback: (factiiiId: number, pos: number) => void
  ) => {
    socket.on('factiiiReordered', callback);
    return () => {
      socket.off('factiiiReordered', callback);
    };
  };

  const onPostEdited = (callback: (post: Post) => void) => {
    socket.on('postEdited', callback);
    return () => {
      socket.off('postEdited', callback);
    };
  };

  const onPostVoted = (callback: (postId: string, count: number) => void) => {
    socket.on('postVoted', callback);
    return () => {
      socket.off('postVoted', callback);
    };
  };

  const onPostDeleted = (callback: (postId: string) => void) => {
    socket.on('postDeleted', callback);
    return () => {
      socket.off('postDeleted', callback);
    };
  };

  // ============================================
  // WebRTC Sync Events
  // ============================================

  const createSyncRoom = (callback: (response: CreateRoomResponse) => void) => {
    socket.emit('createSyncRoom', callback);
  };

  const joinSyncRoom = (
    roomId: string,
    callback: (response: JoinRoomResponse) => void
  ) => {
    socket.emit('joinSyncRoom', { roomId }, callback);
  };

  const leaveSyncRoom = (roomId: string) => {
    socket.emit('leaveSyncRoom', { roomId });
  };

  const emitSyncOffer = (roomId: string, offer: RTCSessionDescriptionInit) => {
    socket.emit('syncOffer', { roomId, offer });
  };

  const emitSyncAnswer = (
    roomId: string,
    answer: RTCSessionDescriptionInit
  ) => {
    socket.emit('syncAnswer', { roomId, answer });
  };

  const emitSyncIceCandidate = (
    roomId: string,
    candidate: RTCIceCandidateInit
  ) => {
    socket.emit('syncIceCandidate', { roomId, candidate });
  };

  const onPeerJoined = (callback: () => void) => {
    socket.on('peerJoined', callback);
    return () => {
      socket.off('peerJoined', callback);
    };
  };

  const onPeerLeft = (callback: () => void) => {
    socket.on('peerLeft', callback);
    return () => {
      socket.off('peerLeft', callback);
    };
  };

  const onSyncOffer = (
    callback: (offer: RTCSessionDescriptionInit) => void
  ) => {
    socket.on('syncOffer', callback);
    return () => {
      socket.off('syncOffer', callback);
    };
  };

  const onSyncAnswer = (
    callback: (answer: RTCSessionDescriptionInit) => void
  ) => {
    socket.on('syncAnswer', callback);
    return () => {
      socket.off('syncAnswer', callback);
    };
  };

  const onSyncIceCandidate = (
    callback: (candidate: RTCIceCandidateInit) => void
  ) => {
    socket.on('syncIceCandidate', callback);
    return () => {
      socket.off('syncIceCandidate', callback);
    };
  };

  // ============================================
  // Scrape Status Events (WEB ONLY: the scrapers are web admin tools)
  // ============================================

  const joinScrapeUpdates = () => {
    socket.emit('joinScrapeUpdates');
  };

  const leaveScrapeUpdates = () => {
    socket.emit('leaveScrapeUpdates');
  };

  const onScrapeStatusUpdate = (
    callback: (status: ScrapeStatusUpdate) => void
  ) => {
    socket.on('scrapeStatusUpdate', callback);
    return () => {
      socket.off('scrapeStatusUpdate', callback);
    };
  };

  // ============================================
  // Verification Events
  // ============================================

  const joinVerificationQueue = () => {
    socket.emit('joinVerificationQueue');
  };

  const leaveVerificationQueue = () => {
    socket.emit('leaveVerificationQueue');
  };

  const joinVerificationSession = (userId: number) => {
    socket.emit('joinVerificationSession', userId);
  };

  const leaveVerificationSession = (userId: number) => {
    socket.emit('leaveVerificationSession', userId);
  };

  const onVerificationQueueChanged = (callback: () => void) => {
    socket.on('verificationQueueChanged', callback);
    return () => {
      socket.off('verificationQueueChanged', callback);
    };
  };

  const onVerificationSessionChanged = (callback: () => void) => {
    socket.on('verificationSessionChanged', callback);
    return () => {
      socket.off('verificationSessionChanged', callback);
    };
  };

  const onVerificationApproved = (
    callback: (payload: {
      approvedBios: string[];
      approvedAvatars: { id: string; bytes: string }[];
    }) => void
  ) => {
    socket.on('verificationApproved', callback);
    return () => {
      socket.off('verificationApproved', callback);
    };
  };

  // A runner reported its configured private connections (claude/onedrive/git).
  const onRunnerConnectionsUpdated = (
    callback: (data: { runnerId: number; connections: string[] }) => void
  ) => {
    socket.on('runnerConnectionsUpdated', callback);
    return () => {
      socket.off('runnerConnectionsUpdated', callback);
    };
  };

  // ============================================
  // Socket Instance Access
  // ============================================

  /** Get the socket ID (useful for registering with server) */
  const getSocketId = () => socket.id;

  /** Check if socket is connected */
  const isConnected = () => socket.connected;

  /** Reconnect if not already connected */
  const reconnect = () => {
    if (!socket.connected) {
      socket.connect();
    }
  };

  // How long to wait for the server's answer to a OneDrive index mint before giving up.
  const ONEDRIVE_MINT_ACK_TIMEOUT_MS = 8000;
  // OneDrive index (desktop): relay change pokes to the shell and mint webhooks on its user socket.
  const onOnedriveChanged = (callback: (spaceSlug: string) => void) => {
    const handler = (data: { spaceSlug: string }) => callback(data.spaceSlug);
    socket.on('onedriveChanged', handler);
    return () => {
      socket.off('onedriveChanged', handler);
    };
  };

  const registerOnedriveWebhook = (
    spaceSlug: string
  ): Promise<{ clientState: string } | null> =>
    new Promise((resolve) => {
      socket
        .timeout(ONEDRIVE_MINT_ACK_TIMEOUT_MS)
        .emit('onedriveWebhookRegister', { spaceSlug }, (err, res) => {
          if (err || !res || 'error' in res) {
            resolve(null);
            return;
          }
          resolve(res);
        });
    });

  // Mint an embed token for the shell's OneDrive index service. Null on any failure.
  const mintEmbedToken = (): Promise<string | null> =>
    new Promise((resolve) => {
      socket.timeout(ONEDRIVE_MINT_ACK_TIMEOUT_MS).emit('embedTokenMint', (err, res) => {
        resolve(err || !res || 'error' in res ? null : res.token);
      });
    });

  return {
    // Global / Session
    onOnedriveChanged,
    registerOnedriveWebhook,
    mintEmbedToken,
    onMessage,
    onRefreshUser,
    onSearchIndexProgress,
    onResearchProgress,
    onResearchResult,
    onResearchError,
    onTronReward,
    onLogout,
    onConnect,
    onNotificationCount,

    // Conversations
    onNewMessage,
    onNewConversation,
    onConversationNameUpdated,
    onMemberAdded,
    onMemberRemoved,
    onBanDisputeRejected,
    onConversationDeleted,
    onMessageAcknowledged,
    onMessageEdited,
    onMessageDeleted,
    onMessageRestored,
    onMessageReaction,
    onThreadReplyCount,
    onTyping,
    emitTyping,
    joinConversationRoom,
    onActivity,

    // Posts
    joinPostStats,
    leavePostStats,
    onRepliesCount,
    onNewReply,
    onVoteCount,

    // Spaces (board-layout realtime channel)
    joinBoardRoom,
    leaveBoardRoom,
    onSpaceMeta,
    onSpaceRules,
    onSpaceMembers,
    onSpaceDeleted,
    onBoardUpdate,
    onPostCreated,
    onPostMoved,
    onPostReordered,
    onPostEdited,
    onPostVoted,
    onPostDeleted,
    onFactiiiReordered,

    // WebRTC Sync
    createSyncRoom,
    joinSyncRoom,
    leaveSyncRoom,
    emitSyncOffer,
    emitSyncAnswer,
    emitSyncIceCandidate,
    onPeerJoined,
    onPeerLeft,
    onSyncOffer,
    onSyncAnswer,
    onSyncIceCandidate,

    // Scrape Status
    joinScrapeUpdates,
    leaveScrapeUpdates,
    onScrapeStatusUpdate,

    // Verification
    joinVerificationQueue,
    leaveVerificationQueue,
    joinVerificationSession,
    leaveVerificationSession,
    onVerificationQueueChanged,
    onVerificationSessionChanged,
    onVerificationApproved,

    // Runners
    onRunnerConnectionsUpdated,

    // Socket instance
    getSocketId,
    isConnected,
    reconnect
  };
}

export default useSocket;
